/* eslint-disable no-useless-escape */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Chip,
  FormControl, InputLabel, TextField,
} from '@mui/material';

import { CommonElementProps } from '../types';
import ElementUtils from '../utils';

const validateEmail = (email: string) => email.match(
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
);

function compareEmailsArray(arr1: string[], arr2: string[]) {
  if (arr1.length !== arr2.length) {
    return false;
  }
  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();
  for (let i = 0; i < sortedArr1.length; i += 1) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }
  return true;
}

export interface EmailsChipsProps extends CommonElementProps<string[]> {
  onChange?: (value: string[]) => void;
  value?: string[];
}

const EmailsChips = function EmailsChips(props: EmailsChipsProps) {
  const {
    onChange,
    placeholder,
    error,
    label,
    disabled,
    value: defaultValue,
  } = props;

  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setEmails(defaultValue || []);
  }, [defaultValue]);

  useEffect(() => {
    if (!compareEmailsArray(emails, defaultValue || [])) {
      if (onChange) onChange(emails);
    }
  }, [emails]);

  const handleOnChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<string> | undefined,
  ) => {
    if (details) {
      const { option } = details;
      if (validateEmail(option)) {
        if (onChange) onChange(value);
        console.log(event, value, reason, details);
      }
    }
  };

  const handleClipboardEvent = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('Text') || '';
    const emailsData = pastedText.split(',').map((s) => s.trim());
    const hasInvalidEmail = emailsData.some((email) => validateEmail(email) === null);

    if (!hasInvalidEmail) {
      setEmails((prevEmails) => {
        const allEmails = [...prevEmails, ...emailsData];
        const uniqueEmails = allEmails.filter((email, index) => allEmails.indexOf(email) === index);
        return uniqueEmails;
      });
    }
    setInputValue('');
  }, [setInputValue]);

  const handleOnBlur = () => {
    if (inputValue && validateEmail(inputValue)) {
      setEmails((prevEmails) => {
        const allEmails = [...prevEmails, inputValue];
        const uniqueEmails = allEmails.filter((email, index) => allEmails.indexOf(email) === index);
        return uniqueEmails;
      });
      setInputValue('');
    }
  };

  return (
    <FormControl className="w-full custom-shadow">
      <InputLabel
        error={!!error}
        color="secondary"
        shrink
        className="text-[1.4rem] font-bold -ml-3 -mt-[14px] text-[#28364C] focus:text-[#28364C]"
      >
        {label}
      </InputLabel>
      <Autocomplete
        disabled={disabled}
        clearIcon={false}
        options={[]}
        value={emails}
        freeSolo
        multiple
        renderTags={(value, prps) => value.map(
          (option, index) => (<Chip label={option} {...prps({ index })} />),
        )}
        clearOnBlur
        clearOnEscape
        onBlur={handleOnBlur}
        inputValue={inputValue}
        onChange={handleOnChange}
        placeholder={placeholder}
        onInputChange={(e, value) => {
          console.log(e);
          setInputValue(value);
        }}
        renderInput={(params) => (
          <TextField
            variant="outlined"
            {...params}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              error: !!error,
              sx: {
                '& fieldset': { border: 'none' },
              },
              classes: {
                root: disabled ? 'pl-0 text-[#28364C] rounded-md' : 'bg-white pl-0 rounded-md',
                error: 'outline outline-[1px] outline-error',
              },
            }}
            onPaste={handleClipboardEvent}
          />
        )}
      />
      {error && (
        <div className="absolute -bottom-5 text-error text-xs">
          {ElementUtils.renderError(error)}
        </div>
      )}
    </FormControl>
  );
};

EmailsChips.defaultProps = {
  onChange: () => { },
  value: [],
};

EmailsChips.propTypes = {

};
export default EmailsChips;
